import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Popover } from 'bootstrap'

import { listTemplates, deleteTemplate, syncTemplates } from '../../../actions/template'
import { cutText } from '../../../helpers/style_string.helper'
import BaseTable from '../BaseTable/BaseTable'
import './TemplatesTable.css'

const STATUS_LABELS = {
  DRAFT: 'Borrador',
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
  DISABLED: 'Deshabilitado',
  PAUSED: 'Pausado'
}

const CATEGORY_LABELS = {
  UTILITY: 'Utilidad',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticación'
}

const LANGUAGE_LABELS = {
  es_CO: 'Español (COL)',
  es_MX: 'Español (MEX)',
  es_AR: 'Español (ARG)',
  es_ES: 'Español (ESP)',
  en_US: 'Inglés (US)',
  pt_BR: 'Portugués (BR)'
}

const KNOWN_STATUSES = new Set(Object.keys(STATUS_LABELS))

function getStatusClass (status) {
  if (!status) return ''
  const key = String(status).toUpperCase()
  return KNOWN_STATUSES.has(key) ? key : ''
}

function escapeHtml (str = '') {
  return String(str)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function LanguagePopoverBadge ({ language, status, bodyText }) {
  const setPopoverState = useRef()
  const popover = useRef()

  const langLabel = LANGUAGE_LABELS[language] ?? language ?? '—'
  const statusLabel = STATUS_LABELS[status] ?? status ?? '—'
  const statusClass = getStatusClass(status)

  const html = useMemo(() => {
    const safeBody = escapeHtml(bodyText || '—').replaceAll('\n', '<br />')
    return `
      <div class="tpl-popover-title">${escapeHtml(langLabel)}</div>
      <div class="tpl-popover-status">
        <span class="tpl-popover-dot${statusClass ? ` status-${escapeHtml(statusClass)}` : ''}"></span>
        <span>${escapeHtml(statusLabel)}</span>
      </div>
      <div class="tpl-popover-section">Body</div>
      <div class="tpl-popover-body">${safeBody}</div>
    `
  }, [langLabel, statusLabel, bodyText, statusClass])

  useEffect(() => {
    let isActive = true
    if (isActive && popover.current) {
      setPopoverState.current = new Popover(popover.current, {
        trigger: 'hover focus',
        placement: 'right',
        html: true,
        customClass: 'templates-lang-popover',
        content: html,
        container: 'body',
        fallbackPlacements: ['left', 'top', 'bottom']
      })
    }
    return function cleanup () {
      isActive = false
      try {
        setPopoverState.current?.dispose?.()
      } catch {}
    }
  }, [html])

  return (
    <span
      ref={popover}
      className='templates-lang-pill'
      data-bs-toggle='popover'
      tabIndex={0}
    >
      <span className={`templates-lang-dot${statusClass ? ` status-${statusClass}` : ''}`} />
      {langLabel}
    </span>
  )
}

function normalizeLanguages (t) {
  // Supports:
  // - current shape (single language row): { language, status, body: { text } }
  // - future shape (multi-language): { languages: [{ language|code, status, bodyText|body:{text} }] }
  if (Array.isArray(t?.languages) && t.languages.length > 0) {
    return t.languages.map(l => ({
      language: l.language ?? l.code ?? l.lang ?? '',
      status: l.status ?? l.approvalStatus ?? '',
      bodyText: l.bodyText ?? l.body?.text ?? ''
    }))
  }
  return [{
    language: t?.language ?? '',
    status: t?.status ?? '',
    bodyText: t?.body?.text ?? ''
  }]
}

function TemplatesTable ({ refreshTrigger }) {
  const client = useSelector((state) => state.client)
  const navigate = useNavigate()

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterCategory, setFilterCategory] = useState('')

  const fetchTemplates = useCallback(async function fetchTemplates () {
    setLoading(true)
    try {
      const { data } = await listTemplates(client._id, {
        status: filterStatus || undefined,
        category: filterCategory || undefined,
        search: search || undefined
      })
      setTemplates(data)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error al cargar templates')
    } finally {
      setLoading(false)
    }
  }, [client._id, filterCategory, filterStatus, search])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates, refreshTrigger])

  async function handleDelete (template) {
    if (!window.confirm(`¿Eliminar el template "${template.name}"?`)) return
    try {
      await deleteTemplate(client._id, template._id)
      toast.success('Template eliminado')
      fetchTemplates()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error al eliminar')
    }
  }

  async function handleSync () {
    setSyncing(true)
    try {
      const { data } = await syncTemplates(client._id)
      toast.success(`Sincronizado: ${data.synced} templates actualizados`)
      fetchTemplates()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error al sincronizar')
    } finally {
      setSyncing(false)
    }
  }

  // Apply client-side search/filter on top of what the server returned
  const visible = templates.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase())
    const matchStatus = !filterStatus || t.status === filterStatus
    const matchCat = !filterCategory || t.category === filterCategory
    return matchSearch && matchStatus && matchCat
  })

  const templateById = useMemo(() => {
    const map = new Map()
    visible.forEach(t => map.set(String(t._id), t))
    return map
  }, [visible])

  const getFullTemplate = useCallback((row) => {
    const id = row?._id
    return templateById.get(String(id)) || row
  }, [templateById])

  const header = useMemo(() => ([
    'Nombre',
    'Body',
    'Idioma',
    'Categoría',
    'Actualizado'
  ]), [])

  const json = useMemo(() => {
    return visible.map(t => ({
      _id: t._id,
      name: cutText(t.name, true, 20),
      body: cutText(t.body?.text, true, 25),
      language: `__LANG__${t._id}`,
      category: CATEGORY_LABELS[t.category] ?? t.category,
      updatedAt: t.updatedAt ? new Date(t.updatedAt).toLocaleDateString('es-CO') : '—'
    }))
  }, [visible])

  function formatRowData (rowData) {
    if (typeof rowData === 'string' && rowData.startsWith('__LANG__')) {
      const id = rowData.replace('__LANG__', '')
      const t = templateById.get(String(id))
      if (!t) return '—'
      return (
        <div className='templates-lang-cell'>
          {normalizeLanguages(t).map((l, idx) => (
            <LanguagePopoverBadge
              key={`${t._id}-${l.language || idx}`}
              language={l.language}
              status={l.status}
              bodyText={l.bodyText}
            />
          ))}
        </div>
      )
    }
    return rowData
  }

  return (
    <div className='templates-table-wrapper'>
      <div className='templates-table-header'>
        <div className='templates-table-actions'>
          <div className='templates-table-actions-content'>
            <input
              placeholder='Buscar por nombre...'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value=''>Todos los estados</option>
              {Object.entries(STATUS_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value=''>Todas las categorías</option>
              {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <div className='templates-table-actions-content'>
            <button className='btn-sync' onClick={handleSync} disabled={syncing}>
              {syncing ? '↻ Sincronizando...' : '↻ Sincronizar'}
            </button>
            <button className='btn main-btn btn-new-template' onClick={() => navigate('/profile/templates/new')}>
              + Nuevo template
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='templates-table-scroll'>
        {loading
          ? <div className='templates-loading'>Cargando...</div>
          : visible.length === 0
            ? (
              <div className='templates-empty'>
                No hay templates.{' '}
                <span
                  style={{ color: 'var(--color-orange)', cursor: 'pointer' }}
                  onClick={() => navigate('/profile/templates/new')}
                >
                  Crea el primero.
                </span>
              </div>
              )
            : (
              <BaseTable
                json={json}
                header={header}
                formatRowData={formatRowData}
                actions={(t) => (
                  <div className='templates-table-options'>
                    <button
                      onClick={() => {
                        const template = getFullTemplate(t)
                        navigate(`/profile/templates/${template._id}/edit`, { state: { template } })
                      }}
                      className='btn main-btn tpl-btn edit-btn text-nowrap'
                      title='Editar'
                      aria-label='Editar'
                    >
                      <i className='fas fa-pen' />
                    </button>
                    <button
                      onClick={() => handleDelete(getFullTemplate(t))}
                      className='btn main-btn tpl-btn delete-btn text-nowrap'
                      title='Eliminar'
                      aria-label='Eliminar'
                    >
                      <i className='fas fa-trash-alt' />
                    </button>
                  </div>
                )}
              />
              )}
      </div>
    </div>
  )
}

export default TemplatesTable
